import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ScheduleDetails } from '../services/pipeline/pipeline.types';

/**
 * Pipeline execution status
 */
export type PipelineStatus =
  | 'idle'
  | 'initializing'
  | 'running'
  | 'stopped'
  | 'failed'
  | 'completed';

/**
 * Pipeline state structure
 */
export interface PipelineState {
  dataFlowId: number | null;
  sessionId: string | null;
  pipelineName: string;
  pipelineDescription: string;
  isRunEnabled: boolean;
  status: PipelineStatus;
  isDataFlowCreated: boolean;
  scheduleDetails?: ScheduleDetails | null;
}

/**
 * Initial state
 */
const initialState: PipelineState = {
  dataFlowId: null,
  sessionId: null,
  pipelineName: '',
  pipelineDescription: '',
  isRunEnabled: false,
  status: 'idle',
  isDataFlowCreated: false,
  scheduleDetails: null,
};

/**
 * Slice
 */
const pipelineSlice = createSlice({
  name: 'pipeline',
  initialState,
  reducers: {
    /**
     * Set pipeline identity
     */
    setPipelineIdentity(
      state,
      action: PayloadAction<{
        dataFlowId: number | null;
        sessionId: string | null;
      }>
    ) {
      state.dataFlowId = action.payload.dataFlowId;
      state.sessionId = action.payload.sessionId;
    },

    /**
     * Set pipeline basic details
     */
    setPipelineDetails(
      state,
      action: PayloadAction<{
        pipelineName: string;
        pipelineDescription: string;
      }>
    ) {
      state.pipelineName = action.payload.pipelineName;
      state.pipelineDescription = action.payload.pipelineDescription;
    },

    /**
     * Enable / disable run
     */
    setRunEnabled(state, action: PayloadAction<boolean>) {
      state.isRunEnabled = action.payload;
    },

    /**
     * Set pipeline status
     */
    setStatus(state, action: PayloadAction<PipelineStatus>) {
      state.status = action.payload;
    },

    /**
     * Bulk update pipeline state
     */
    setPipelineState(state, action: PayloadAction<Partial<PipelineState>>) {
      return { ...state, ...action.payload };
    },

    setDataFlowCreated(state, action: PayloadAction<boolean>) {
      state.isDataFlowCreated = action.payload;
    },

    setScheduleDetails(state, action: PayloadAction<ScheduleDetails | null>) {
      state.scheduleDetails = action.payload;
    },

    /**
     * Reset entire pipeline state
     */
    resetPipelineState() {
      return initialState;
    },
  },
});

/**
 * Actions
 */
export const {
  setPipelineIdentity,
  setPipelineDetails,
  setRunEnabled,
  setStatus,
  setPipelineState,
  setDataFlowCreated,
  setScheduleDetails,
  resetPipelineState,
} = pipelineSlice.actions;

/**
 * Reducer
 */
export default pipelineSlice.reducer;
